import React from 'react';
import { config, styled } from '../../../stitches.config';
import { TinaField } from '../../../shared/types';
import { Button } from '../../atoms/Button/Button';
import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';
import { Section } from 'react-headings';
import { CopyText, Heading } from 'shared/Typography';
import slugify from 'slugify';
import { Anchor } from '../../atoms/Anchor/Anchor';
import { useEditState } from 'tinacms/dist/react';
import { removeAssetTinaIoFromImageUrl } from '../../../shared/Helper';

export type Content9Props = {
  title?: string | null;
  subtitle?: string | null;
  buttonLabel?: string | null;
  buttonHref?: string | null;
  imageSrc?: string | null;
  imageAlt?: string | null;
  imagePosition?: string | null;
  textPosition?: 'left' | 'right' | null;
  children: React.ReactNode;
} & TinaField;

const InfoLayout = styled('div', {});

const ImageLayout = styled('div', {
  position: 'relative',
  aspectRatio: 4 / 5,
});

const ContentLayout = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$spacing-3x',
  [`& ${ImageLayout}`]: {
    height: '200px',
  },
  [`& ${InfoLayout}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: '$spacing-3x',
  },
  '@tabletAndUp': {
    display: 'grid',
    gap: 0,
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'top',
    justifyItems: 'center',
    [`& ${ImageLayout}`]: {
      width: '100%',
      height: 'auto',
      paddingBlock: '$spacing-8x',
    },
    '@desktopAndUp': {
      paddingBlock: '$spacing-14x',
    },
  },
  variants: {
    textPosition: {
      left: {
        '@tabletAndUp': {
          [`& ${InfoLayout}`]: {
            order: 1,
            paddingRight: '$spacing-8x',
          },
          [`& ${ImageLayout}`]: {
            order: 2,
          },
        },
        '@desktopAndUp': {
          [`& ${InfoLayout}`]: {
            paddingRight: '$spacing-14x',
          },
        },
      },
      right: {
        '@tabletAndUp': {
          [`& ${InfoLayout}`]: {
            order: 2,
            paddingLeft: '$spacing-8x',
          },
          [`& ${ImageLayout}`]: {
            order: 1,
          },
        },
        '@desktopAndUp': {
          [`& ${InfoLayout}`]: {
            paddingLeft: '$spacing-14x',
          },
        },
      },
    },
  },
});

const StyledHeading = styled(Heading, {
  marginBottom: '$spacing-2x',
});

const StyledCopyText = styled('div', {
  fontSize: '$xs',
  lineHeight: '$xs',
  '> p': {
    marginBottom: '1rem',
  },
});

const TextLayout = styled(Section, {});

export const Content9: React.FC<Content9Props> = ({
  title,
  subtitle,
  buttonLabel,
  buttonHref,
  imageSrc,
  imagePosition,
  imageAlt,
  textPosition = 'left',
  children,
}) => {
  const { edit: isInAdminEditMode } = useEditState();
  const imageSrcWithoutAssetTinaIo = removeAssetTinaIoFromImageUrl(
    imageSrc ?? '',
  );

  return (
    <>
      <ContentLayout textPosition={textPosition === 'left' ? 'left' : 'right'}>
        <ImageLayout>
          {title && <Anchor id={slugify(title)} />}
          {imageSrc && imageAlt && isInAdminEditMode && (
            <ExportedImage
              src={imageSrc}
              alt={imageAlt}
              layout="fill"
              objectFit="cover"
              objectPosition={imagePosition ?? undefined}
              sizes={`${config.media.tabletAndUp} 50vw, 100vw`}
            />
          )}
          {imageSrc && imageAlt && !isInAdminEditMode && (
            <ExportedImage
              src={imageSrcWithoutAssetTinaIo}
              alt={imageAlt}
              layout="fill"
              objectFit="cover"
              objectPosition={imagePosition ?? undefined}
              sizes={`${config.media.tabletAndUp} 50vw, 100vw`}
            />
          )}
        </ImageLayout>
        <InfoLayout>
          <div>
            <TextLayout
              component={
                <StyledHeading variant={{ '@initial': 'h2' }}>
                  {title ? (
                    <Link href={`#${slugify(title)}`} scroll={false}>
                      {title}
                    </Link>
                  ) : (
                    title
                  )}
                </StyledHeading>
              }
            >
              {subtitle && <CopyText variant="subtitle">{subtitle}</CopyText>}
              <StyledCopyText>{children}</StyledCopyText>
            </TextLayout>
          </div>
          {buttonLabel && buttonHref && (
            <Link href={buttonHref}>
              <Button>{buttonLabel}</Button>
            </Link>
          )}
        </InfoLayout>
      </ContentLayout>
    </>
  );
};
